import type { ReactNode } from 'react'
import type { BoxProps } from '@chakra-ui/react'
import { ModalLayer } from './ModalLayer'

type AccessibleName =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-label'?: never; 'aria-labelledby': string }

export type SideDrawerProps = AccessibleName & {
  open: boolean
  onClose: () => void
  children: ReactNode
  id?: string
  'aria-describedby'?: string
  width?: BoxProps['width']
  bg?: BoxProps['bg']
  /** Scrim layer; the panel sits 50 above it. */
  zIndex?: number
  /** Blocks scrim and Escape dismissal, e.g. during a save. */
  dismissible?: boolean
  /** Preserve content while closed, hidden from focus and interaction. */
  keepMounted?: boolean
  /** Dim-and-dismiss scrim behind the panel. Off for a docked panel the page stays usable beside. */
  scrim?: boolean
}

/** A right-hand slide-over on ModalLayer. It never locks or hides the page globally. */
export function SideDrawer({
  width = { base: '100vw', md: '420px' },
  bg = 'bg.surface',
  zIndex = 1300,
  keepMounted = false,
  scrim = true,
  ...layerProps
}: SideDrawerProps) {
  return (
    <ModalLayer
      {...layerProps}
      placement="end"
      zIndex={zIndex}
      keepMounted={keepMounted}
      backdropProps={scrim ? undefined : { display: 'none' }}
      h="100dvh"
      w={width}
      maxW="100vw"
      bg={bg}
      color="fg"
      borderLeftWidth="1px"
      borderColor="border"
      boxShadow="-8px 0 24px rgba(0, 0, 0, 0.3)"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      transform={layerProps.open ? 'translateX(0)' : 'translateX(100%)'}
      transition={keepMounted ? 'transform 0.25s ease' : undefined}
      css={{ '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}
    />
  )
}
